import { HttpClient, HttpEvent } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable, timeout } from 'rxjs';
import {
    API_BASE_URL,
    GetClinicalFeedbackOrdersRequest,
    ControlCentreApiClient,
    PaginatedResponseOfGetClinicalFeedbackOrdersResponse,
    PostClinicalFeedbackRequest,
    FeedbackType,
    PostClinicalFeedbackResponse,
    DeleteClinicalFeedbackAttachmentRequest,
    AwsS3ObjectPreSignedUrl
} from '@app/api-module/control-centre-api-client';
import { ClinicalFeedbackConstants } from './clinical-feedback-constants';
import { FeedbackTypeOption, SearchOrderResponse } from './clinical-feedback.model';

@Injectable({
    providedIn: 'root'
})
export class ClinicalFeedbackService {
    readonly baseUrl: string;

    constructor(
        private readonly controlCentreApiClient: ControlCentreApiClient,
        private readonly httpClient: HttpClient,
        @Inject(API_BASE_URL) baseUrl: string
    ) {
        this.baseUrl = baseUrl;
    }

    getExistingClinicalFeedback(orgCode: string, siteIds: number[]): Observable<SearchOrderResponse[]> {
        return this.mapToSearchOrderResponse(this.controlCentreApiClient.clinicalFeedback_GetDefaultClinicalFeedback(orgCode, siteIds));
    }

    searchStudy(searchStudyRequest: GetClinicalFeedbackOrdersRequest): Observable<SearchOrderResponse[]> {
        return this.mapToSearchOrderResponse(this.controlCentreApiClient.clinicalFeedback_GetOrdersForClinicalFeedback(searchStudyRequest));
    }

    private mapToSearchOrderResponse(
        getOrdersAndFeedbackCall: Observable<PaginatedResponseOfGetClinicalFeedbackOrdersResponse>
    ): Observable<SearchOrderResponse[]> {
        return getOrdersAndFeedbackCall.pipe(timeout(ClinicalFeedbackConstants.HTTP_TIMEOUT_MSECONDS)).pipe(
            map((response: PaginatedResponseOfGetClinicalFeedbackOrdersResponse) =>
                response.items.map(
                    item =>
                        new SearchOrderResponse({
                            patientMrn: item.patientId,
                            accessionNumber: item.accessionNumber,
                            trimmedAccessionNumber: item.trimmedAccessionNumber,
                            patientFirstName: item.patientFirstName,
                            patientLastName: item.patientLastName,
                            patientBirthDate: item.patientDateOfBirth,
                            feedbackStatus: item.feedbackStatus,
                            feedbackSeriousIncidentType: item.feedbackSeriousIncidentType,
                            feedbackType: item.feedbackType,
                            feedbackId: item.clinicalFeedbackId,
                            reportingRadiologist: item.radiologist,
                            studyDate: item.studyDateTime,
                            studyType: item.studyType,
                            urgency: item.urgency,
                            modality: item.modality,
                            region: item.region,
                            everlightResponses: item.everlightResponses
                        })
                )
            )
        );
    }

    uploadAttachment(formData: FormData): Observable<HttpEvent<unknown>> {
        const fileUploadUrl = '/control-centre-api/v1/clinical-feedback/attachments'; // TODO: Use client instead
        return this.httpClient.post(this.baseUrl + fileUploadUrl, formData, {
            reportProgress: true,
            observe: 'events'
        });
    }

    deleteAttachment(accessionNumber: string, key: string): Observable<void> {
        return this.controlCentreApiClient.clinicalFeedback_DeleteAttachments(
            new DeleteClinicalFeedbackAttachmentRequest({
                accessionNumber: accessionNumber,
                documentId: key
            })
        );
    }

    saveClinicalFeedback(
        accessionNumber: string,
        feedbackType: FeedbackTypeOption,
        feedbackJson: string
    ): Observable<PostClinicalFeedbackResponse> {
        const request = new PostClinicalFeedbackRequest({
            accessionNumber: accessionNumber,
            feedbackType: FeedbackType[feedbackType],
            feedbackJson: feedbackJson
        });
        return this.controlCentreApiClient.clinicalFeedback_PostClinicalFeedback(request);
    }

    getDownloadLink(accessionNumber: string, documentId: string): Observable<AwsS3ObjectPreSignedUrl> {
        return this.controlCentreApiClient
            .clinicalFeedback_GetEverlightResponseDocumentUrl(accessionNumber, documentId)
            .pipe(timeout(ClinicalFeedbackConstants.HTTP_TIMEOUT_MSECONDS));
    }

    getDownloadCustomerFeedbackLink(clinicalFeedbackId: string): Observable<AwsS3ObjectPreSignedUrl> {
        return this.controlCentreApiClient
            .clinicalFeedback_GetCustomerFeedbackDocumentUrl(clinicalFeedbackId)
            .pipe(timeout(ClinicalFeedbackConstants.HTTP_TIMEOUT_MSECONDS));
    }
}
